// frontend/src/pages/AgenteForm.jsx
import React, { useEffect, useMemo, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || "";

function getMe() {
  try {
    // molti progetti salvano l'oggetto user del login in localStorage come "me" o "user"
    const raw =
      localStorage.getItem("me") ||
      localStorage.getItem("user") ||
      localStorage.getItem("USER");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function AgenteForm({ initial = null, onSuccess }) {
  const editing = Boolean(initial?.id);
  const me = getMe();
  const isMadre = (me?.role || "").toUpperCase() === "MADRE";

  const [form, setForm] = useState({
    email: initial?.email || "",
    username: initial?.username || "",
    first_name: initial?.first_name || "",
    last_name: initial?.last_name || "",
    phone: initial?.phone || "",
    role: (initial?.role || "AGENTE").toUpperCase(),
    // se NON sono MADRE e sto creando: precompilo con il mio store e blocco l'input
    store_name:
      initial?.store_name ||
      initial?.store ||
      (!editing && !isMadre ? me?.store_name || "" : ""),
    percentuale: initial?.percentuale ?? "",
    acquired_company_percent: initial?.acquired_company_percent ?? "8.00",
    assigned_company_percent: initial?.assigned_company_percent ?? "5.00",
    is_active: initial?.is_active ?? true,
    password: "",
    note: initial?.note || "",
  });

  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const suggestedUser = useMemo(() => {
    const p = (form.email || "").split("@")[0] || "";
    return p.replace(/[^a-z0-9._-]/gi, "").toLowerCase();
  }, [form.email]);

  useEffect(() => {
    if (!editing && !form.username && suggestedUser) {
      setForm((f) => ({ ...f, username: suggestedUser }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedUser]);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const generatePwd = () => {
    const s = Math.random().toString(36).slice(-4);
    const n = Math.random().toString(36).toUpperCase().slice(-4);
    const sym = "!@#$%^&*"[Math.floor(Math.random() * 8)];
    setForm((f) => ({ ...f, password: `Ag${s}${n}${sym}` }));
    setShowPwd(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem("access");
      const url = editing
        ? `${API_URL}/api/agenti/${initial.id}/`
        : `${API_URL}/api/agenti/`;
      const method = editing ? "PUT" : "POST";

      const payload = {
        email: form.email,
        username: form.username,
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone || undefined,
        role: form.role,
        store_name: form.store_name || undefined,
        percentuale:
          form.percentuale === "" ? undefined : String(form.percentuale),
        acquired_company_percent:
          form.acquired_company_percent === ""
            ? undefined
            : String(form.acquired_company_percent),
        assigned_company_percent:
          form.assigned_company_percent === ""
            ? undefined
            : String(form.assigned_company_percent),
        is_active: !!form.is_active,
        note: form.note || undefined,
      };
      if (form.password.trim()) payload.password = form.password.trim();

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok)
        throw new Error(data.detail || data.error || "Errore salvataggio");

      alert(editing ? "✅ Agente aggiornato." : "✅ Agente creato.");
      onSuccess?.();
    } catch (err) {
      alert("❌ " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card shadow-sm border-0">
      <div className="card-header bg-white">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h5 className="m-0">{editing ? "Modifica Agente" : "Nuovo Agente"}</h5>
            <small className="text-muted">
              Compila i dati anagrafici e le impostazioni dell’account.
            </small>
          </div>
        </div>
      </div>

      <form className="card-body" onSubmit={handleSubmit} autoComplete="off">
        {!editing && <input type="password" style={{ display: "none" }} />}

        {/* Dati principali */}
        <div className="mb-3">
          <h6 className="text-uppercase text-muted mb-2">Dati principali</h6>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Email *</label>
              <input
                type="email"
                className="form-control"
                required
                value={form.email}
                onChange={set("email")}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Username *</label>
              <div className="input-group">
                <input
                  className="form-control"
                  required
                  value={form.username}
                  onChange={set("username")}
                />
                {!editing && suggestedUser && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() =>
                      setForm((f) => ({ ...f, username: suggestedUser }))
                    }
                    title="Usa suggerito"
                  >
                    Suggerisci
                  </button>
                )}
              </div>
              <small className="text-muted">
                Suggerito: <code>{suggestedUser || "—"}</code>
              </small>
            </div>

            <div className="col-md-4">
              <label className="form-label">Nome</label>
              <input
                className="form-control"
                value={form.first_name}
                onChange={set("first_name")}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Cognome</label>
              <input
                className="form-control"
                value={form.last_name}
                onChange={set("last_name")}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Telefono</label>
              <input
                className="form-control"
                value={form.phone}
                onChange={set("phone")}
              />
            </div>
          </div>
        </div>

        {/* Profilo & permessi */}
        <div className="mb-3">
          <h6 className="text-uppercase text-muted mb-2">Profilo & Permessi</h6>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Ruolo *</label>
              <select
                className="form-select"
                value={form.role}
                onChange={set("role")}
                required
              >
                <option value="AGENTE">AGENTE</option>
                <option value="FRANCHISING">FRANCHISING</option>
                <option value="AZIENDA">AZIENDA</option>
                <option value="PV">PV</option>
                <option value="MADRE">SEDE</option>
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label">Store (label)</label>
              <input
                className="form-control"
                placeholder="Es. ZioRoby"
                value={form.store_name}
                onChange={set("store_name")}
                readOnly={!isMadre} // non-MADRE: campo bloccato (ma è sempre un input testo)
              />
              <small className="text-muted">
                Solo etichetta: se il backend usa un ID store, si potrà adeguare.
              </small>
            </div>

            <div className="col-12">
              <hr className="my-1" />
              <h6 className="text-uppercase text-muted mb-1">
                Provvigioni B2B
              </h6>
              <small className="text-muted">
                Le percentuali possono essere personalizzate per questo agente.
              </small>
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Ordini procurati personalmente dall'agente
              </label>
              <div className="input-group">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="form-control"
                  value={form.acquired_company_percent}
                  onChange={set("acquired_company_percent")}
                />
                <span className="input-group-text">%</span>
              </div>
              <small className="text-muted">
                Predefinita 8%. Per ordini raccolti personalmente dall'agente,
                con firma del cliente e verifica commerciale della SEDE.
              </small>
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Ordini autonomi delle aziende acquisite
              </label>
              <div className="input-group">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="form-control"
                  value={form.assigned_company_percent}
                  onChange={set("assigned_company_percent")}
                />
                <span className="input-group-text">%</span>
              </div>
              <small className="text-muted">
                Predefinita 5%. Per ordini autonomi entro sei mesi
                dall'acquisizione approvata dalla SEDE, con attività
                commerciale verificata. Escluse le aziende solo assegnate.
              </small>
            </div>

            <div className="col-md-4">
              <label className="form-label">
                Commissione legacy %
              </label>
              <div className="input-group">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="form-control"
                  value={form.percentuale}
                  onChange={set("percentuale")}
                />
                <span className="input-group-text">%</span>
              </div>
              <small className="text-muted">
                Compatibilit? con il vecchio sistema. Non viene usata per le
                nuove provvigioni B2B aziendali.
              </small>
            </div>

            <div className="col-12">
              <div className="form-check">
                <input
                  id="ag-active"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.is_active}
                  onChange={set("is_active")}
                />
                <label htmlFor="ag-active" className="form-check-label">
                  Account attivo
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Sicurezza */}
        <div className="mb-3">
          <h6 className="text-uppercase text-muted mb-2">Sicurezza</h6>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">
                {editing ? "Nuova password (opzionale)" : "Password iniziale *"}
              </label>
              <div className="input-group">
                <input
                  type={showPwd ? "text" : "password"}
                  className="form-control"
                  placeholder={editing ? "Lascia vuoto per non cambiare" : ""}
                  value={form.password}
                  onChange={set("password")}
                  required={!editing}
                  autoComplete={editing ? "off" : "new-password"}
                  name={`pwd_${editing ? "edit" : "create"}`}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPwd((s) => !s)}
                >
                  {showPwd ? "Nascondi" : "Mostra"}
                </button>
                {!editing && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={generatePwd}
                  >
                    Genera
                  </button>
                )}
              </div>
              {!editing && (
                <small className="text-muted">
                  Verrà usata solo alla creazione (puoi comunicarla o inviare un invito).
                </small>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">Note</label>
              <textarea
                rows={1}
                className="form-control"
                value={form.note}
                onChange={set("note")}
              />
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
            title={editing ? "Aggiorna" : "Salva"}
          >
            {saving ? "Salvataggio…" : editing ? "Aggiorna" : "Salva"}
          </button>
        </div>
      </form>
    </div>
  );
}
